import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CreditCard, Loader2, CheckCircle2, Clock, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_DOMIUM = `${process.env.REACT_APP_BACKEND_URL}/api/domium-ia/rappel-paiement`;
const fmt = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');

export default function PayerEcheancePage({ apiBase = API_DOMIUM }) {
  const API = apiBase;
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/${token}`).then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || 'Lien de paiement invalide'));
  }, [token]);

  const pay = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/${token}`, { origin: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { setError(e.response?.data?.detail || 'Paiement indisponible'); setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-slate-50 py-12 px-4" data-testid="payer-echeance-page">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
        <div className="flex items-center gap-2 text-emerald-700"><ShieldCheck className="h-5 w-5" /><span className="text-sm font-semibold">O'SCOP Outremer — Paiement sécurisé</span></div>
        {!data && !error && <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />}
        {error && <p className="text-red-600 text-sm" data-testid="payer-echeance-error">{error}</p>}
        {data && (
          <>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Échéance {data.echeance}/4</h1>
              <p className="text-sm text-slate-600">Dossier <strong data-testid="payer-echeance-reference">{data.reference}</strong>{data.demandeur ? ` — ${data.demandeur}` : ''}</p>
            </div>
            <div className="rounded-lg bg-emerald-50 p-4 flex items-center justify-between">
              <span className="text-sm text-emerald-900">Montant de l'échéance (prévue le {fmtD(data.date_echeance)})</span>
              <span className="text-xl font-bold text-emerald-800" data-testid="payer-echeance-montant">{fmt(data.montant)}</span>
            </div>
            {data.regularisation && (
              <p className="text-xs text-red-700 bg-red-50 rounded p-2" data-testid="payer-echeance-regul">
                Contrat résilié — réactivation : échéances dues {fmt(data.regularisation.echeances_dues)} + contribution suspensive {fmt(data.regularisation.contribution_suspensive)}.
              </p>
            )}
            <p className="text-xs text-slate-500">Déjà réglé : {fmt(data.paiement?.paye_ttc)} · Reste : {fmt(data.paiement?.reste_ttc)}</p>
            {data.payee ? (
              <p className="flex items-center gap-2 text-emerald-700 bg-emerald-50 rounded p-3 text-sm" data-testid="payer-echeance-payee"><CheckCircle2 className="h-4 w-4" /> Cette échéance est déjà réglée. Merci !</p>
            ) : data.expire ? (
              <p className="flex items-center gap-2 text-amber-800 bg-amber-50 rounded p-3 text-sm" data-testid="payer-echeance-expire"><Clock className="h-4 w-4" /> Ce lien a expiré le {fmtD(data.expires_at)} (validité 7 jours). Contactez votre conseiller pour en recevoir un nouveau.</p>
            ) : (
              <>
                <Button onClick={pay} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="payer-echeance-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />} Payer {fmt(data.montant)} par carte
                </Button>
                <p className="text-[11px] text-slate-500">Lien valable jusqu'au {fmtD(data.expires_at)}. Un reçu PDF vous sera envoyé par email après le paiement.</p>
              </>
            )}
          </>
        )}
      </div>
    </main>
  );
}
